import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { resetConsent } from '../lib/metaPixel';

const Section = ({ number, title, children }) => (
  <section className="mb-10">
    <h2 className="font-heading text-xl font-semibold text-primary mb-4">
      {number}. {title}
    </h2>
    {children}
  </section>
);

const SubHeading = ({ children }) => (
  <h3 className="font-heading font-semibold text-text-main mb-2 mt-5">{children}</h3>
);

const BulletList = ({ items }) => (
  <ul className="space-y-1.5 ml-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2.5 text-text-main text-sm leading-relaxed">
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Para = ({ children }) => (
  <p className="text-text-main text-sm leading-relaxed mb-3">{children}</p>
);

const PrivacyPage = ({ onNavigate }) => {
  return (
    <div className="min-h-screen bg-background">
      {/* Subtle teal-to-lavender gradient strip at top */}
      <div className="h-1 w-full" style={{ background: 'linear-gradient(to right, #4AADA8, #A99CC8)' }} aria-hidden="true" />

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Back button */}
        {onNavigate && (
          <button
            onClick={() => onNavigate('feed')}
            aria-label="Back to feed"
            className="flex items-center gap-2 text-sm text-text-muted hover:text-primary transition-colors mb-10 min-h-[44px]"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back to Sirati
          </button>
        )}

        {/* Header */}
        <header className="text-center mb-12">
          <p className="font-heading text-3xl font-bold tracking-wide text-primary mb-1">SIRATI</p>
          <h1 className="font-heading text-2xl font-semibold mb-2" style={{ color: '#A99CC8' }}>Privacy Policy</h1>
          <p className="text-xs text-text-muted">Effective Date: May 2026 &nbsp;|&nbsp; Last Updated: October 2026</p>
        </header>

        {/* Divider */}
        <div className="h-px mb-10" style={{ background: 'linear-gradient(to right, transparent, #4AADA820, #A99CC830, #4AADA820, transparent)' }} aria-hidden="true" />

        {/* Policy body */}
        <div>

          <Section number="1" title="Introduction">
            <Para>
              Welcome to Sirati (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). Sirati is a faith-centered community app for single Muslims focused on personal growth and marriage readiness, accessible at www.sirati.co.
            </Para>
            <Para>
              This Privacy Policy explains how we collect, use, store, and protect your personal information when you use the Sirati platform. By creating an account or using our services, you agree to the practices described in this policy.
            </Para>
            <Para>
              If you do not agree with this policy, please do not use Sirati.
            </Para>
          </Section>

          <Section number="2" title="Information We Collect">
            <SubHeading>Information you provide directly:</SubHeading>
            <BulletList items={[
              'Name and display name',
              'Email address',
              'Gender (used to provide gender-aware community features)',
              'Profile information including photo, bio, and preferences',
              'Onboarding responses (marriage timeline, growth focus, financial goals, spiritual baseline)',
              'Community posts, comments, and direct messages',
              'Payment information processed securely through our third-party payment provider',
              'Event registrations: which events you register for, the amount you paid, whether you attended, and whether you opted out of event photos and video',
              'Library activity: which Library items you download or buy, when, and what you paid',
              'Your gender and display name are also used to manage capacity and attendance for events, including events separated by gender',
            ]} />

            <SubHeading>Information collected automatically:</SubHeading>
            <BulletList items={[
              'Device type and browser information',
              'IP address and general location (country/region level only)',
              'App usage data including features accessed and time spent',
              'Session cookies required for authentication and app functionality',
              'Analytics data to improve app performance and user experience',
              'With your consent, advertising measurement data from the Meta Pixel: pages viewed, and when you sign up or subscribe, along with browser and device details',
            ]} />
          </Section>

          <Section number="3" title="How We Use Your Information">
            <Para>We use the information we collect to:</Para>
            <BulletList items={[
              'Create and manage your Sirati account',
              'Provide personalized features including your Marriage Readiness Score and dashboard',
              'Enable community features such as posts, comments, spaces, and direct messaging',
              'Track your learning progress through the LMS modules',
              'Process payments and manage your subscription',
              'Manage event registrations, tickets, refunds, reminders, and attendance',
              'Provide Library downloads and purchases, check that you have access to an item, and process refunds',
              'Send important account and service notifications',
              'Improve app features, performance, and user experience',
              'Ensure platform safety by detecting and preventing misuse',
            ]} />
          </Section>

          <Section number="4" title="How We Share Your Information">
            <Para>We do not sell your personal information. We may share data only in the following limited circumstances:</Para>
            <BulletList items={[
              'With service providers who help operate our platform (authentication, payments, hosting) under strict confidentiality agreements',
              'With Stripe, our payment processor, to handle subscription billing, event tickets, Library purchases, and refunds securely',
              'With Kit, our email provider: when you create an account, we add your email address and display name to our email list with a "Member" label. When you register for an event or download or buy something from the Library, we also add a label (for example, that you downloaded a Library item). We use these labels so we can send you related updates. We never send your files this way. If you use a free tool such as the Wifey Application Builder or the Marriage Readiness Quiz, we add your email address to our list with a label for that tool; for the quiz we also record your overall stage and the area to grow next, while your individual answers stay on your device. You can unsubscribe at any time using the link in any email',
              'We do not share your information with partner venues. If a venue ever needs something to run an event, such as a check-in list, we will share only the minimum, like your display name, and nothing else',
              'With Meta (Facebook and Instagram), only if you accept advertising cookies: we share event information (such as completing sign-up or subscribing), a hashed form of your email address, and browser details so we can measure and improve our ads. We never share your onboarding answers, posts, comments, or messages with Meta',
              'With Rewardful, our affiliate referral programme provider — when you join Sirati via a referral link, Rewardful receives anonymised attribution data (no personal profile information is shared)',
              'When required by law or to respond to valid legal process',
              'To protect the rights, safety, or property of Sirati, our users, or the public',
              'With your explicit consent for any purpose not described here',
            ]} />
            <Para>
              Community posts and profile information you choose to make public are visible to other Sirati members based on your visibility settings.
            </Para>
          </Section>

          <Section number="5" title="Data Security">
            <Para>
              We take reasonable technical and organizational measures to protect your personal information against unauthorized access, loss, or misuse. Your data is stored securely using Supabase infrastructure with encrypted connections.
            </Para>
            <Para>
              Passwords are never stored in plain text. Payment information is handled entirely by Stripe and never stored on our servers.
            </Para>
            <Para>
              While we work hard to protect your data, no method of transmission over the internet is 100% secure. We encourage you to use a strong, unique password for your account.
            </Para>
          </Section>

          <Section number="6" title="Data Retention">
            <Para>
              We retain your personal information for as long as your account is active or as needed to provide our services. If you delete your account, we will remove your personal data within 30 days, except where retention is required by law or legitimate business purposes such as fraud prevention.
            </Para>
            <Para>
              When you delete your account, your profile and personal information are permanently deleted within 30 days. Posts you have made in the community are anonymized rather than deleted — they are attributed to a former member handle rather than your identity. If you would like your posts permanently deleted, please contact us at salam@sirati.co and we will remove them within 30 days.
            </Para>
            <Para>
              Records of event, Library and subscription payments and refunds may be kept for as long as the law requires for tax and accounting, even after you delete your account. Other event registration details that identify you are removed with the rest of your personal data within 30 days.
            </Para>
            <Para>
              Anonymized or aggregated data that cannot identify you may be retained indefinitely for product improvement purposes.
            </Para>
          </Section>

          <Section number="7" title="Your Rights and Choices">
            <Para>Depending on your location, you may have the right to:</Para>
            <BulletList items={[
              'Access the personal information we hold about you',
              'Correct inaccurate or incomplete information',
              'Request deletion of your account and associated data',
              'Withdraw consent for optional data uses at any time',
              'Export a copy of your data',
              'Opt out of non-essential communications, including event emails (use the unsubscribe link in the email, or write to privacy@sirati.co)',
            ]} />
            <Para>
              To exercise any of these rights, contact us at privacy@sirati.co. We will respond within 30 days.
            </Para>
            <Para>
              <strong>Cookie choices.</strong> You can accept or decline advertising and analytics cookies in the banner shown on your first visit.{' '}
              <button
                type="button"
                onClick={() => { resetConsent(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="text-primary underline underline-offset-2"
                data-testid="change-cookie-choice"
              >
                Change your cookie choice
              </button>
              . Declining stops the Meta Pixel and server-side advertising events, and turns off our analytics tool for your browser.
            </Para>
          </Section>

          <Section number="8" title="Children's Privacy">
            <Para>
              Sirati is intended for users aged 18 and older. We do not knowingly collect personal information from anyone under 18. If we become aware that a minor has created an account, we will promptly delete their information and close the account.
            </Para>
          </Section>

          <Section number="9" title="Changes to This Policy">
            <Para>
              We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. We will notify you of significant changes by posting a notice in the app or sending an email to your registered address. The &ldquo;Last Updated&rdquo; date at the top of this page indicates when it was most recently revised.
            </Para>
            <Para>
              Continued use of Sirati after changes are posted constitutes your acceptance of the updated policy.
            </Para>
          </Section>

          <Section number="10" title="Contact Us">
            <Para>If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your data, please contact us:</Para>
            <div className="mt-3 p-4 rounded-arch-lg border border-primary/20 bg-primary/5 text-sm text-text-main space-y-1">
              <p><span className="font-medium text-primary">Sirati</span></p>
              <p>Email: <a href="mailto:privacy@sirati.co" className="text-primary hover:underline">privacy@sirati.co</a></p>
              <p>Website: <a href="https://www.sirati.co" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.sirati.co</a></p>
            </div>
          </Section>

        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-border text-center">
          <p className="text-xs text-text-muted">
            &copy; {new Date().getFullYear()} Sirati. 🌙 All Rights Reserved.
          </p>
          {onNavigate && (
            <button
              onClick={() => onNavigate('feed')}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white transition-opacity hover:opacity-90 min-h-[44px]"
              style={{ background: 'linear-gradient(135deg, #4AADA8, #A99CC8)' }}
            >
              Return to Sirati
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
